import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Markdown from './Markdown';
import usePageMeta from '../hooks/usePageMeta';
import JsonLd from './JsonLd';
import Footer from './Footer';
import BookCallModal from './BookCallModal';
import { getLocationBySlug } from '../data/locations';
import servicesData from '../data/services';
import { SITE_URL, business } from '../data/business';
import './MarketingPage.css';

const LocationPage = () => {
  const { slug: routeSlug } = useParams();
  // Location routes are registered by explicit path, so derive the slug from the
  // path when there's no :slug param.
  const slug = routeSlug || window.location.pathname.replace(/^\/+|\/+$/g, '');
  const loc = getLocationBySlug(slug);
  const [isModalOpen, setIsModalOpen] = useState(false);

  usePageMeta(
    loc
      ? {
          title: `${loc.title} | Second Life Software`,
          description: loc.metaDescription,
          path: `/${loc.slug}`,
        }
      : {}
  );

  if (!loc) {
    return <Navigate to="/" replace />;
  }

  const pageUrl = `${SITE_URL}/${loc.slug}`;

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: `${business.name} — ${loc.city}`,
    description: loc.metaDescription,
    url: pageUrl,
    telephone: business.telephone,
    email: business.email,
    image: `${SITE_URL}/images/og-image.png`,
    priceRange: '$$',
    areaServed: { '@type': 'City', name: loc.city },
    address: { '@type': 'PostalAddress', addressLocality: loc.city, addressRegion: loc.region, addressCountry: 'US' },
    parentOrganization: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: business.name },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: business.rating.value,
      reviewCount: String(business.rating.count),
      bestRating: '5',
      worstRating: '1',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: loc.title, item: pageUrl },
    ],
  };

  const faqSchema = loc.faq && loc.faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: loc.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <div className="mkt-page">
      <JsonLd id={`loc-${loc.slug}`} data={localBusinessSchema} />
      <JsonLd id={`loc-breadcrumb-${loc.slug}`} data={breadcrumbSchema} />
      {faqSchema && <JsonLd id={`loc-faq-${loc.slug}`} data={faqSchema} />}

      <header className="mkt-topbar">
        <Link to="/" className="mkt-logo">Second Life Software</Link>
        <button className="mkt-topbar-btn" onClick={() => setIsModalOpen(true)}>
          Book a Call
        </button>
      </header>

      <section className="mkt-hero">
        <span className="mkt-eyebrow">{loc.city}, {loc.region}</span>
        <h1 className="mkt-h1">{loc.title}</h1>
        <p className="mkt-sub">{loc.intro}</p>
        <button className="mkt-cta-btn" onClick={() => setIsModalOpen(true)}>
          Book a Free Consultation
        </button>
      </section>

      <div className="mkt-body">
        <Markdown>{loc.body}</Markdown>
      </div>

      {loc.faq && loc.faq.length > 0 && (
        <section className="mkt-faq">
          <h2>Frequently asked questions</h2>
          {loc.faq.map((f, i) => (
            <div key={i} className="mkt-faq-item">
              <p className="mkt-faq-q">{f.q}</p>
              <p className="mkt-faq-a">{f.a}</p>
            </div>
          ))}
        </section>
      )}

      <section className="mkt-cta">
        <h2 className="mkt-cta-title">Building something in {loc.city}?</h2>
        <p className="mkt-cta-text">
          Tell us what you're trying to build and we'll give you an honest scope,
          cost range, and timeline. No obligation.
        </p>
        <button className="mkt-cta-btn" onClick={() => setIsModalOpen(true)}>
          Book a Free Call
        </button>
      </section>

      <nav className="mkt-related">
        <p className="mkt-related-title">What we build</p>
        <div className="mkt-related-links">
          {servicesData.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`}>{s.name}</Link>
          ))}
        </div>
      </nav>

      <Footer />
      <BookCallModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LocationPage;
